import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClientAuth } from '../../../contexts/ClientAuthContext';
import { Building2, User, ArrowRight } from 'lucide-react';

// "Mis datos" — data ownership/export + account info.
// Property editing lives in ONE place: /portal/manage/properties.
export default function ManageSettingsPage() {
  const { t } = useTranslation();
  const { user } = useClientAuth();

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-12">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t('portal.manage.settings.title')}</h1>
        <p className="text-slate-500 mt-1">{t('portal.manage.settings.subtitle')}</p>
      </div>

      {/* Data ownership / portability — export everything anytime, no lock-in */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-slate-900">{t('portal.manage.settings.dataTitle')}</h2>
        <p className="text-sm text-slate-500 mt-1">
          {t('portal.manage.settings.dataDesc')}
        </p>
        <a
          href="/api/portal/manage/export"
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium transition-colors"
        >
          {t('portal.manage.settings.exportData')}
        </a>
      </div>

      {/* Properties live in one place — link there instead of a duplicate form */}
      <Card icon={Building2} title={t('portal.manage.settings.propertiesTitle', 'Tus alojamientos')}>
        <p className="text-sm text-slate-500 -mt-1">
          {t('portal.manage.settings.propertiesDesc', 'Gestiona los datos, fotos y precios de tus alojamientos en un solo lugar.')}
        </p>
        <Link
          to="/portal/manage/properties"
          className="inline-flex items-center gap-2 mt-1 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium transition-colors"
        >
          {t('portal.manage.settings.manageProperties', 'Gestionar mis alojamientos')} <ArrowRight size={16} />
        </Link>
      </Card>

      {/* Owner (read-only) */}
      <Card icon={User} title={t('portal.manage.settings.cardOwner')}>
        <Grid>
          <Field label={t('portal.manage.settings.fieldName')}><input className={`${inp} bg-slate-50`} value={user?.name ?? ''} readOnly /></Field>
          <Field label={t('portal.manage.settings.fieldEmail')}><input className={`${inp} bg-slate-50`} value={user?.email ?? ''} readOnly /></Field>
        </Grid>
        <p className="text-xs text-slate-400">{t('portal.manage.settings.ownerNote')}</p>
      </Card>
    </div>
  );
}

const inp = 'w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm';
const Grid = ({ children }: { children: React.ReactNode }) => <div className="grid sm:grid-cols-2 gap-3">{children}</div>;

function Card({ icon: Icon, title, children }: { icon: typeof User; title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 space-y-3">
      <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Icon size={18} className="text-teal-600" /> {title}</h2>
      {children}
    </section>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="block text-sm font-medium text-slate-600 mb-1">{label}</label>{children}</div>;
}
